import { ParameterElastic_Schema } from "@/services/parameters/parametersSchema";
import { FilterDefinition_Schema } from "@/services/filter";
import { ApiResponse_Image_Schema } from "@/services/images";
import { MarketplaceItemElastic_Schema } from "@/services/marketplace/item/marketplaceItemSchema";
import { UserId_Schema } from "@/services/user/userIdSchema";
import { Nickname_Schema } from "@/services/user/profile";
import { Rating_Schema } from "@/services/user/rating";
import { DateTimeIso8601Local_Schema, WholeNumber_Schema } from "@/utilities/schema";
import { z } from "zod";
import { CategoryId_Schema } from "@/services/collection/categories/categoriesSchema";
import { Theme_Schema } from "@/services/themes/themeSchema";
import { CollectibleElastic_Schema } from "@/services/collectibles/elastic";
import { createNumericPrice, PriceNumericOrEmpty_Schema } from "@/services/prices";

export const UserStanding_Schema = z
  .object({
    score: WholeNumber_Schema,
    rank: WholeNumber_Schema,
  })
  .meta({ openApi: "UserStanding" });

export const OwnerElastic_Schema = z
  .object({
    id: UserId_Schema,
    nickname: Nickname_Schema,
    rating: Rating_Schema,
    ratingCount: WholeNumber_Schema,
    avatarUrl: z.string().transform((val) => (val === "" ? null : val)),
    totalCollectionCount: WholeNumber_Schema,
    userStanding: UserStanding_Schema.nullable(),
  })
  .transform(({ totalCollectionCount, userStanding, ...rest }) => ({
    ...rest,
    itemsCount: totalCollectionCount, // Just to align it with UserPublicInfo - you can drop this when UserPublicInfo is not used anymore
    standing: userStanding,
  }));

export const CollectionItemElastic_Schema = z
  .object({
    itemId: WholeNumber_Schema,
    name: z.string(),
    acquirePrice: PriceNumericOrEmpty_Schema,
    currentPrice: PriceNumericOrEmpty_Schema,
    collectibleName: z.string(),
    collectibleElastic: CollectibleElastic_Schema.nullable(),
    description: z.string(),
    note: z.string(),
    category: CategoryId_Schema,
    theme: Theme_Schema.nullable(),
    identification: z.array(z.string()),
    images: z.array(ApiResponse_Image_Schema),
    // API may send an object (empty or keyed by string) instead of an array — normalize to array
    parameters: z.preprocess((val) => (val && typeof val === "object" && !Array.isArray(val) ? Object.values(val) : val), ParameterElastic_Schema.array()),
    owner: OwnerElastic_Schema,
    added: z.string(),
    marketPlaceItem: MarketplaceItemElastic_Schema.nullable(),
    likedByCurrentUser: z.boolean().nullable(),
    likesCount: WholeNumber_Schema,
    publishedDate: DateTimeIso8601Local_Schema.nullable(),
    archivedAt: DateTimeIso8601Local_Schema.nullable(),
    quantity: WholeNumber_Schema,

    // There is more, feel free to add them if needed.
  })
  .meta({
    openapi: "CollectionItemElasticReadResponse",
  })
  .transform(({ marketPlaceItem, itemId, likedByCurrentUser, category, added, publishedDate, currentPrice, acquirePrice, collectibleElastic, archivedAt, ...rest }) => ({
    ...rest,
    collectible: collectibleElastic,

    /**
     * You should never read this property directly. You should use useCollectionItemElastic() to extract marketplace item information.
     * Bacause Collection Item can have Marketplace item even if it is not for sale!!
     **/
    marketplaceItem: marketPlaceItem,

    id: itemId,
    likedByCurrentUser: likedByCurrentUser ?? false, // We don't want to work with null

    /** @deprecated Keep it for backward compatibility. Use categoryId instead */
    category,

    categoryId: category,

    dateAdded: added,
    datePublished: publishedDate,
    dateArchived: archivedAt,
    estimatedPrice: currentPrice === null ? null : createNumericPrice(currentPrice.price / 100, currentPrice.currencyId), // We dont want it in cents
    acquirePrice: acquirePrice === null ? null : createNumericPrice(acquirePrice.price / 100, acquirePrice.currencyId), // We dont want it in cents
  }));

export const CollectionItemElasticSearchResponse_Schema = z.object({
  totalItems: WholeNumber_Schema,
  items: z.array(CollectionItemElastic_Schema),
  parameters: z.array(FilterDefinition_Schema),
  aggregations: z.array(z.object({ name: z.string(), value: WholeNumber_Schema })),
});

export type OwnerElastic = z.infer<typeof OwnerElastic_Schema>;
export type CollectionItemElastic = z.infer<typeof CollectionItemElastic_Schema>;
export type CollectionItemElasticSearchResponse = z.infer<typeof CollectionItemElasticSearchResponse_Schema>;
